import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { getProductsApi, createProductApi, updateProductApi, deleteProductApi, uploadProductImgApi, updateInventoryApi, getVariantsApi, createVariantApi, updateVariantApi, deleteVariantApi, updateVariantSchemaApi } from '../../api/productApi';
import { getCategoriesApi } from '../../api/categoryApi';
import AdminModal, { ConfirmModal } from './AdminModal';
import imgUrl from '../../utils/imgUrl';

const EMPTY = { name:'', slug:'', category_id:'', price:'', compare_price:'', description:'', brand:'Bhadralok', sku:'', stock:'0', is_active:true, is_featured:false };

/* ─── Attribute types the admin can choose ─── */
const ATTR_TYPES = [
  { value:'color',  label:'Color (swatch)' },
  { value:'size',   label:'Size (buttons)' },
  { value:'select', label:'Select (dropdown)' },
  { value:'text',   label:'Text (free input)' },
  { value:'number', label:'Number' },
];

/* ─── Build an empty variant form from a schema ─── */
function emptyForm(schema) {
  const attrs = {};
  schema.forEach((a) => {
    attrs[a.name] = '';
    if (a.type === 'color') attrs[`${a.name}_label`] = '';
  });
  return { ...attrs, stock: '0', price_adjustment: '0' };
}

/* ═══════════════════════════════════════════════════════
   VariantsModal — fully dynamic attribute management
═══════════════════════════════════════════════════════ */
function VariantsModal({ product, onClose }) {
  /* variants + loading */
  const [variants,  setVariants]  = useState([]);
  const [vLoading,  setVLoading]  = useState(true);

  /* schema editing */
  const [schema,      setSchema]      = useState(
    Array.isArray(product.variant_attributes) ? product.variant_attributes : []
  );
  const [schemaTab,   setSchemaTab]   = useState(false); // toggle schema editor
  const [schemaSaving,setSchemaSaving]= useState(false);

  /* variant add/edit form */
  const [form,    setForm]    = useState(() => emptyForm(schema));
  const [editId,  setEditId]  = useState(null);
  const [delVid,  setDelVid]  = useState(null);
  const [saving,  setSaving]  = useState(false);

  /* ── load variants ── */
  const loadVariants = () => {
    setVLoading(true);
    getVariantsApi(product.id)
      .then((r) => setVariants(r.data))
      .catch(() => toast.error('Failed to load variants'))
      .finally(() => setVLoading(false));
  };
  useEffect(() => { loadVariants(); }, []); // eslint-disable-line

  /* ── schema helpers ── */
  const addAttr = () => setSchema((s) => [...s, { name:'', type:'text', label:'', options:'' }]);
  const removeAttr = (i) => setSchema((s) => s.filter((_, idx) => idx !== i));
  const updateAttr = (i, key, val) => setSchema((s) => s.map((a, idx) => idx === i ? { ...a, [key]: val } : a));

  const saveSchema = async () => {
    const cleaned = schema
      .filter((a) => a.name.trim())
      .map((a) => ({
        name:    a.name.trim().toLowerCase().replace(/\s+/g, '_'),
        type:    a.type,
        label:   a.label || a.name,
        options: a.type === 'size' || a.type === 'select'
          ? (typeof a.options === 'string' ? a.options.split(',').map((o) => o.trim()).filter(Boolean) : a.options || [])
          : undefined,
      }));
    setSchemaSaving(true);
    try {
      await updateVariantSchemaApi(product.id, { variant_attributes: cleaned });
      setSchema(cleaned);
      setForm(emptyForm(cleaned));
      setEditId(null);
      toast.success('Attribute schema saved');
      setSchemaTab(false);
    } catch { toast.error('Schema save failed'); }
    finally { setSchemaSaving(false); }
  };

  /* ── variant form helpers ── */
  const setAttrVal = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const openEdit = (v) => {
    setEditId(v.id);
    const attrs = typeof v.attributes === 'string' ? JSON.parse(v.attributes) : (v.attributes || {});
    setForm({ ...emptyForm(schema), ...attrs, stock: v.stock, price_adjustment: v.price_adjustment || 0 });
  };
  const resetForm = () => { setEditId(null); setForm(emptyForm(schema)); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const attributes = {};
    schema.forEach((a) => {
      attributes[a.name] = form[a.name] || '';
      if (a.type === 'color') attributes[`${a.name}_label`] = form[`${a.name}_label`] || '';
    });
    const payload = { attributes, stock: parseInt(form.stock) || 0, price_adjustment: parseFloat(form.price_adjustment) || 0 };
    try {
      if (editId) { await updateVariantApi(product.id, editId, payload); toast.success('Variant updated'); }
      else        { await createVariantApi(product.id, payload);          toast.success('Variant added');   }
      resetForm();
      loadVariants();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await deleteVariantApi(product.id, delVid);
      toast.success('Variant deleted');
      setDelVid(null);
      loadVariants();
    } catch { toast.error('Delete failed'); }
  };

  /* ── render one attribute input in the form ── */
  const renderAttrInput = (attr) => {
    const val = form[attr.name] ?? '';
    const opts = Array.isArray(attr.options) ? attr.options : (attr.options || '').split(',').map((o) => o.trim()).filter(Boolean);

    if (attr.type === 'color') return (
      <div key={attr.name} style={{ flex:'1', minWidth:'140px' }}>
        <label style={VS.lbl}>{attr.label || attr.name} (label)</label>
        <input style={VS.inp} placeholder="e.g. Olive Green" value={form[`${attr.name}_label`] || ''} onChange={(e) => setAttrVal(`${attr.name}_label`, e.target.value)} />
        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'6px' }}>
          <input type="color" value={val || '#000000'} style={{ width:'36px', height:'32px', border:'1.5px solid #E5E5E5', cursor:'pointer', padding:'2px', flexShrink:0 }}
            onChange={(e) => setAttrVal(attr.name, e.target.value)} />
          <span style={{ fontSize:'11px', color:'#888' }}>{val || '#000000'}</span>
        </div>
      </div>
    );

    if (attr.type === 'size' && opts.length > 0) return (
      <div key={attr.name} style={{ flex:'1', minWidth:'100px' }}>
        <label style={VS.lbl}>{attr.label || attr.name}</label>
        <select style={VS.inp} value={val} onChange={(e) => setAttrVal(attr.name, e.target.value)} required>
          <option value="">— select —</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );

    if (attr.type === 'select' && opts.length > 0) return (
      <div key={attr.name} style={{ flex:'1', minWidth:'100px' }}>
        <label style={VS.lbl}>{attr.label || attr.name}</label>
        <select style={VS.inp} value={val} onChange={(e) => setAttrVal(attr.name, e.target.value)} required>
          <option value="">— select —</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );

    if (attr.type === 'number') return (
      <div key={attr.name} style={{ flex:'1', minWidth:'80px' }}>
        <label style={VS.lbl}>{attr.label || attr.name}</label>
        <input style={VS.inp} type="number" value={val} onChange={(e) => setAttrVal(attr.name, e.target.value)} />
      </div>
    );

    /* text / fallback */
    return (
      <div key={attr.name} style={{ flex:'1', minWidth:'100px' }}>
        <label style={VS.lbl}>{attr.label || attr.name}</label>
        <input style={VS.inp} value={val} onChange={(e) => setAttrVal(attr.name, e.target.value)} />
      </div>
    );
  };

  /* ── render one variant card ── */
  const renderVariantCard = (v) => {
    const attrs = typeof v.attributes === 'string' ? JSON.parse(v.attributes) : (v.attributes || {});
    const colorAttr = schema.find((a) => a.type === 'color');
    const colorHex  = colorAttr ? attrs[colorAttr.name] : null;
    const colorLabel = colorAttr ? (attrs[`${colorAttr.name}_label`] || colorHex) : null;

    /* summary line: all non-label attr values */
    const summary = schema
      .filter((a) => a.type !== 'color')
      .map((a) => attrs[a.name])
      .filter(Boolean)
      .join(' · ');

    return (
      <div key={v.id} style={{ ...VS.varCard, borderColor: v.stock === 0 ? '#FECACA' : '#E5E5E5' }}>
        {/* color swatch if present */}
        {colorHex && (
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
            <div style={{ width:'14px', height:'14px', borderRadius:'50%', background: colorHex, border:'1px solid rgba(0,0,0,.15)', flexShrink:0 }} />
            <span style={{ fontSize:'11px', color:'#555', fontWeight:600 }}>{colorLabel}</span>
          </div>
        )}
        {/* other attrs */}
        {summary && (
          <div style={{ fontFamily:'var(--font-display)', fontSize:'16px', letterSpacing:'1px', marginBottom:'4px' }}>{summary}</div>
        )}
        {/* if no schema, show raw attributes */}
        {!colorHex && !summary && (
          <div style={{ fontSize:'11px', color:'#aaa', marginBottom:'4px' }}>
            {Object.entries(attrs).map(([k,vv]) => `${k}: ${vv}`).join(', ') || '—'}
          </div>
        )}
        {/* stock */}
        <div style={{ fontSize:'11px', fontWeight:700, color: v.stock === 0 ? '#EF4444' : v.stock < 5 ? '#D97706' : '#16A34A' }}>
          {v.stock === 0 ? 'OUT OF STOCK' : `${v.stock} units`}
        </div>
        {parseFloat(v.price_adjustment) !== 0 && (
          <div style={{ fontSize:'10px', color:'#888', marginTop:'2px' }}>
            {parseFloat(v.price_adjustment) > 0 ? '+' : ''}₹{parseFloat(v.price_adjustment).toLocaleString('en-IN')}
          </div>
        )}
        {/* actions */}
        <div style={{ display:'flex', gap:'3px', marginTop:'6px', paddingTop:'6px', borderTop:'1px solid #F3F4F6' }}>
          <button style={VS.iconBtn} onClick={() => openEdit(v)} title="Edit">✏️</button>
          <button style={{ ...VS.iconBtn, color:'#EF4444' }} onClick={() => setDelVid(v.id)} title="Delete">🗑</button>
        </div>
      </div>
    );
  };

  return (
    <AdminModal title={`VARIANTS — ${product.name}`} onClose={onClose} width="820px">
      {delVid && <ConfirmModal message="Delete this variant? Cannot be undone." onConfirm={handleDelete} onCancel={() => setDelVid(null)} />}

      {/* ── SECTION 1: Attribute Schema ── */}
      <div style={VS.section}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: schemaTab ? '14px' : 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <p style={VS.sectionTitle}>ATTRIBUTE SCHEMA</p>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {schema.map((a) => (
                <span key={a.name} style={{ background:'#EEF2FF', color:'#4338CA', fontSize:'10px', fontWeight:700, padding:'2px 8px', letterSpacing:'.5px' }}>
                  {a.label || a.name} ({a.type})
                </span>
              ))}
              {schema.length === 0 && <span style={{ fontSize:'12px', color:'#aaa' }}>No attributes defined</span>}
            </div>
          </div>
          <button style={VS.outlineBtn} onClick={() => setSchemaTab((v) => !v)}>
            {schemaTab ? 'Close' : '⚙ Edit Schema'}
          </button>
        </div>

        {schemaTab && (
          <div style={{ marginTop:'14px' }}>
            {schema.map((attr, i) => (
              <div key={i} style={{ display:'flex', gap:'8px', alignItems:'flex-end', marginBottom:'8px', flexWrap:'wrap' }}>
                <div style={{ flex:'1', minWidth:'100px' }}>
                  {i === 0 && <label style={VS.lbl}>Attribute Name</label>}
                  <input style={VS.inp} placeholder="e.g. color" value={attr.name}
                    onChange={(e) => updateAttr(i, 'name', e.target.value)} />
                </div>
                <div style={{ flex:'1', minWidth:'100px' }}>
                  {i === 0 && <label style={VS.lbl}>Display Label</label>}
                  <input style={VS.inp} placeholder="e.g. Colour" value={attr.label}
                    onChange={(e) => updateAttr(i, 'label', e.target.value)} />
                </div>
                <div style={{ flex:'1', minWidth:'100px' }}>
                  {i === 0 && <label style={VS.lbl}>Type</label>}
                  <select style={VS.inp} value={attr.type} onChange={(e) => updateAttr(i, 'type', e.target.value)}>
                    {ATTR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                {(attr.type === 'size' || attr.type === 'select') && (
                  <div style={{ flex:'2', minWidth:'160px' }}>
                    {i === 0 && <label style={VS.lbl}>Options (comma-separated)</label>}
                    <input style={VS.inp} placeholder="XS, S, M, L, XL, XXL"
                      value={Array.isArray(attr.options) ? attr.options.join(', ') : (attr.options || '')}
                      onChange={(e) => updateAttr(i, 'options', e.target.value)} />
                  </div>
                )}
                <button type="button" style={{ ...VS.iconBtn, color:'#EF4444', fontSize:'16px', paddingBottom:'8px' }}
                  onClick={() => removeAttr(i)}>✕</button>
              </div>
            ))}
            <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
              <button type="button" style={VS.outlineBtn} onClick={addAttr}>+ Add Attribute</button>
              <button type="button" style={VS.saveBtn} onClick={saveSchema} disabled={schemaSaving}>
                {schemaSaving ? 'Saving…' : 'Save Schema'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── SECTION 2: Add / Edit Variant ── */}
      <div style={VS.section}>
        <p style={VS.sectionTitle}>{editId ? 'EDIT VARIANT' : 'ADD VARIANT'}</p>
        {schema.length === 0 ? (
          <p style={{ fontSize:'13px', color:'#aaa' }}>Define attributes above before adding variants.</p>
        ) : (
          <form onSubmit={handleSave}>
            <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'flex-end' }}>
              {schema.map((attr) => renderAttrInput(attr))}
              <div style={{ flex:'0 0 90px' }}>
                <label style={VS.lbl}>Stock</label>
                <input style={VS.inp} type="number" min="0" value={form.stock}
                  onChange={(e) => setAttrVal('stock', e.target.value)} required />
              </div>
              <div style={{ flex:'0 0 100px' }}>
                <label style={VS.lbl}>Price Adj (₹)</label>
                <input style={VS.inp} type="number" step="0.01" value={form.price_adjustment}
                  onChange={(e) => setAttrVal('price_adjustment', e.target.value)} />
              </div>
              <div style={{ display:'flex', gap:'6px', alignSelf:'flex-end' }}>
                <button type="submit" style={VS.saveBtn} disabled={saving}>
                  {saving ? '…' : editId ? 'Update' : 'Add'}
                </button>
                {editId && <button type="button" style={VS.outlineBtn} onClick={resetForm}>Cancel</button>}
              </div>
            </div>
          </form>
        )}
      </div>

      {/* ── SECTION 3: Variants List ── */}
      <div style={VS.section}>
        <p style={VS.sectionTitle}>ALL VARIANTS ({variants.length})</p>
        {vLoading ? (
          <p style={{ color:'#aaa', fontSize:'13px' }}>Loading…</p>
        ) : variants.length === 0 ? (
          <p style={{ color:'#aaa', fontSize:'13px' }}>No variants yet.</p>
        ) : (
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            {variants.map((v) => renderVariantCard(v))}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

const VS = {
  lbl:         { display:'block', fontSize:'10px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'#6B7280', marginBottom:'5px' },
  inp:         { width:'100%', border:'1.5px solid #E5E5E5', background:'#fff', padding:'8px 10px', fontFamily:'var(--font-body)', fontSize:'13px', outline:'none', boxSizing:'border-box' },
  saveBtn:     { padding:'9px 18px', background:'#111', color:'#fff', border:'none', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' },
  outlineBtn:  { padding:'8px 16px', background:'#fff', border:'1.5px solid #E5E5E5', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:600, cursor:'pointer', color:'#374151', whiteSpace:'nowrap' },
  section:     { background:'#FAFAFA', border:'1px solid #E5E5E5', padding:'16px', marginBottom:'14px' },
  sectionTitle:{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#6B7280', margin:'0 0 12px' },
  varCard:     { border:'1px solid #E5E5E5', background:'#fff', padding:'10px 12px', minWidth:'110px', maxWidth:'180px' },
  iconBtn:     { background:'none', border:'none', cursor:'pointer', fontSize:'13px', padding:'2px 4px', opacity:.7 },
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'');

export default function ProductsTab() {
  const [products, setProducts]   = useState([]);
  const [cats, setCats]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [loading, setLoading]     = useState(true);

  const [modal, setModal]             = useState(null); // null | 'add' | 'edit'
  const [editing, setEditing]         = useState(EMPTY);
  const [delId, setDelId]             = useState(null);
  const [saving, setSaving]           = useState(false);
  const [imgFile, setImgFile]         = useState(null);
  const [variantProduct, setVariantProduct] = useState(null);
  const imgRef = useRef();

  const LIMIT = 12;

  const load = () => {
    setLoading(true);
    getProductsApi({ search, category: catFilter, page, limit: LIMIT, sort: 'newest' })
      .then((r) => { setProducts(r.data.products || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, catFilter, page]); // eslint-disable-line
  useEffect(() => { getCategoriesApi().then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openAdd  = () => { setEditing(EMPTY); setImgFile(null); setModal('add'); };
  const openEdit = (p) => {
    setEditing({ name: p.name, slug: p.slug, category_id: p.category_id, price: p.price, compare_price: p.compare_price || '', description: p.description || '', brand: p.brand || 'Bhadralok', sku: p.sku || '', stock: p.stock || 0, is_active: p.is_active, is_featured: p.is_featured, _id: p.id });
    setImgFile(null);
    setModal('edit');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, price: parseFloat(editing.price), compare_price: editing.compare_price ? parseFloat(editing.compare_price) : null, stock: parseInt(editing.stock) };
      let saved;
      if (modal === 'add') {
        saved = await createProductApi(payload);
        toast.success('Product created!');
      } else {
        await updateProductApi(editing._id, payload);
        if (editing.stock !== undefined) await updateInventoryApi(editing._id, { quantity: parseInt(editing.stock) });
        toast.success('Product updated!');
        saved = { data: { id: editing._id } };
      }
      if (imgFile) {
        const fd = new FormData();
        fd.append('images', imgFile);
        await uploadProductImgApi(saved.data.id, fd);
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await deleteProductApi(delId);
      toast.success('Product deleted');
      setDelId(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Products</h1>
          <p style={S.sub}>{total} products total</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}>+ Add Product</button>
      </div>

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.searchWrap}>
          <span style={S.searchIco}>🔍</span>
          <input style={S.searchInp} placeholder="Search products..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select style={S.select} value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>{['','Product','Category','Price','Stock','Status','Featured','Actions'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '48px', color: '#aaa' }}>Loading...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '48px', color: '#aaa' }}>No products found</td></tr>
            ) : products.map((p) => (
              <tr key={p.id} style={S.tr}>
                <td style={{ ...S.td, width: '52px' }}>
                  <div style={S.thumb}>
                    {p.image ? <img src={imgUrl(p.image)} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ fontSize:'18px' }}>👕</span>}
                  </div>
                </td>
                <td style={S.td}>
                  <p style={{ fontWeight: 700, marginBottom: '2px', color: '#111' }}>{p.name}</p>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>SKU: {p.sku || '—'}</p>
                </td>
                <td style={S.td}><span style={S.catBadge}>{p.category_name}</span></td>
                <td style={S.td}>
                  <p style={{ fontWeight: 700, margin: 0 }}>₹{parseFloat(p.price).toLocaleString('en-IN')}</p>
                  {p.compare_price && <p style={{ fontSize: '11px', color: '#aaa', textDecoration: 'line-through', margin: 0 }}>₹{parseFloat(p.compare_price).toLocaleString('en-IN')}</p>}
                </td>
                <td style={S.td}>
                  <span style={{ ...S.stockBadge, background: p.stock === 0 ? '#FEE2E2' : p.stock <= 5 ? '#FEF3C7' : '#DCFCE7', color: p.stock === 0 ? '#DC2626' : p.stock <= 5 ? '#D97706' : '#16A34A' }}>
                    {p.stock === 0 ? 'Out of Stock' : `${p.stock} units`}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ ...S.pill, background: p.is_active ? '#DCFCE7' : '#F3F4F6', color: p.is_active ? '#16A34A' : '#9CA3AF' }}>
                    {p.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={S.td}><span style={{ fontSize: '16px' }}>{p.is_featured ? '⭐' : '—'}</span></td>
                <td style={S.td}>
                  <div style={S.actionRow}>
                    <button style={S.iconBtn} onClick={() => openEdit(p)} title="Edit">✏️</button>
                    <button style={{ ...S.iconBtn, color: '#7C3AED' }} onClick={() => setVariantProduct(p)} title="Manage Variants">🎨</button>
                    <button style={{ ...S.iconBtn, color: '#EF4444' }} onClick={() => setDelId(p.id)} title="Delete">🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={S.pagination}>
          <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
          <span style={{ fontSize: '13px', color: '#666' }}>Page {page} of {totalPages}</span>
          <button style={S.pgBtn} disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <AdminModal title={modal === 'add' ? 'ADD NEW PRODUCT' : 'EDIT PRODUCT'} onClose={() => setModal(null)} width="680px">
          <form onSubmit={handleSave}>
            <div style={S.formRow}>
              <div style={{ flex: 2 }}>
                <label style={S.lbl}>Product Name *</label>
                <input style={S.inp} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: slugify(e.target.value) })} required />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>SKU</label>
                <input style={S.inp} value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} />
              </div>
            </div>
            <label style={S.lbl}>Slug</label>
            <input style={S.inp} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} required />

            <div style={S.formRow}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Category *</label>
                <select style={S.inp} value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} required>
                  <option value="">Select category</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Brand</label>
                <input style={S.inp} value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} />
              </div>
            </div>

            <div style={S.formRow}>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Selling Price (₹) *</label>
                <input style={S.inp} type="number" step="0.01" min="0" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} required />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Compare Price (₹)</label>
                <input style={S.inp} type="number" step="0.01" min="0" value={editing.compare_price} onChange={(e) => setEditing({ ...editing, compare_price: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.lbl}>Stock Quantity</label>
                <input style={S.inp} type="number" min="0" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} />
              </div>
            </div>

            <label style={S.lbl}>Description</label>
            <textarea style={{ ...S.inp, height: '90px', resize: 'vertical' }} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />

            <div style={S.formRow}>
              <label style={S.checkRow}>
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                <span style={S.checkLbl}>Active (visible to customers)</span>
              </label>
              <label style={S.checkRow}>
                <input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} />
                <span style={S.checkLbl}>Featured on homepage</span>
              </label>
            </div>

            <label style={S.lbl}>Product Image</label>
            <div style={S.fileArea} onClick={() => imgRef.current?.click()}>
              {imgFile ? (
                <img src={URL.createObjectURL(imgFile)} alt="" style={{ height: '80px', objectFit: 'cover' }} />
              ) : (
                <p style={{ color: '#9CA3AF', fontSize: '13px' }}>Click to upload image</p>
              )}
              <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setImgFile(e.target.files[0])} />
            </div>

            <div style={S.formActions}>
              <button type="button" style={S.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" style={S.saveBtn} disabled={saving}>{saving ? 'Saving...' : modal === 'add' ? 'Create Product' : 'Save Changes'}</button>
            </div>
          </form>
        </AdminModal>
      )}

      {delId && (
        <ConfirmModal
          message="This will permanently delete the product and all its images. This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDelId(null)}
        />
      )}

      {variantProduct && (
        <VariantsModal product={variantProduct} onClose={() => setVariantProduct(null)} />
      )}
    </div>
  );
}

const S = {
  hdr:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' },
  title:      { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:        { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  addBtn:     { background: '#111', color: '#fff', border: 'none', padding: '10px 20px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
  filterBar:  { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  searchWrap: { flex: 1, minWidth: '220px', position: 'relative' },
  searchIco:  { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px' },
  searchInp:  { width: '100%', border: '1.5px solid #E5E5E5', background: '#fff', padding: '9px 12px 9px 36px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  select:     { border: '1.5px solid #E5E5E5', background: '#fff', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', cursor: 'pointer' },
  tableWrap:  { background: '#fff', border: '1px solid #E5E5E5', overflow: 'auto' },
  table:      { width: '100%', borderCollapse: 'collapse', minWidth: '800px' },
  th:         { padding: '10px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #E5E5E5', textAlign: 'left', background: '#FAFAFA', whiteSpace: 'nowrap' },
  tr:         { borderBottom: '1px solid #F9FAFB', transition: 'background .1s' },
  td:         { padding: '12px 14px', fontSize: '13px', verticalAlign: 'middle' },
  thumb:      { width: '44px', height: '52px', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 },
  catBadge:   { background: '#EEF2FF', color: '#4338CA', fontSize: '10px', fontWeight: 700, padding: '3px 8px', letterSpacing: '.5px' },
  stockBadge: { display: 'inline-block', fontSize: '11px', fontWeight: 700, padding: '3px 8px' },
  pill:       { display: 'inline-block', fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '99px' },
  actionRow:  { display: 'flex', gap: '4px' },
  iconBtn:    { background: '#F9FAFB', border: '1px solid #E5E5E5', padding: '5px 8px', fontSize: '14px', cursor: 'pointer', transition: 'background .15s' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '16px' },
  pgBtn:      { background: '#fff', border: '1.5px solid #E5E5E5', padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .15s' },
  formRow:    { display: 'flex', gap: '12px', marginBottom: '0', alignItems: 'flex-start' },
  lbl:        { display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#6B7280', marginBottom: '5px', marginTop: '12px' },
  inp:        { width: '100%', border: '1.5px solid #E5E5E5', background: '#FAFAFA', padding: '9px 11px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s' },
  checkRow:   { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1, marginTop: '14px' },
  checkLbl:   { fontSize: '13px', color: '#374151', userSelect: 'none' },
  fileArea:   { border: '2px dashed #E5E5E5', padding: '20px', textAlign: 'center', cursor: 'pointer', marginTop: '4px', transition: 'border-color .2s', background: '#FAFAFA' },
  formActions:{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' },
  cancelBtn:  { padding: '9px 20px', background: '#fff', border: '1.5px solid #E5E5E5', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: '#374151' },
  saveBtn:    { padding: '9px 24px', background: '#111', color: '#fff', border: 'none', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
};
